// externals
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { SocketManager, SocketManagerV2 } from '@makemydeal/dr-shared-network';

// libraries
import { bootstrapActionTypes, dealerSelectors } from '@makemydeal/dr-shared-store';
import type { DashMiddleware, DashStore, DashNext } from '@makemydeal/dr-shared-store';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { BOOTSTRAP_INITIAL_LOAD } from '@makemydeal/dr-shared-store/actionTypes/bootstrapActionTypes';

// actions
import * as networkActions from '../actions/networkActions';
import * as socketActionCreators from '../actions/socketActionCreators';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

// selectors
import * as configSelectors from '../selectors/config';
import * as offerReduxSelectors from '../selectors/offerRedux';
import * as offerSelectors from '../selectors/offerSelectors';
import { getDealXgId } from '@makemydeal/dr-shared-ui-utils/offerSessionUtils/offerSessionUtils';
import { getGlobalFeatureToggle } from '@makemydeal/dr-shared-ui-utils';
import {
    getAsyncScenarioExpectedIds,
    getAsyncScenarioReceivedIds,
    getAsyncScenarioMissingIds,
    hasAsyncScenarioLoadCompleted
} from '../selectors/asyncScenarioLoadSelectors';

// action types
import { SCENARIO_HYDRATE_RECEIVED } from '../actionTypes/asyncScenarioLoadActionTypes';
import { SCENARIOS_INGEST_COMPLETE } from '../actionTypes/multiScenarioActionTypes';
import { OFFER_SAVE_SUCCESS } from '../actionTypes/offerActionTypes';

const { BOOTSTRAP_SUCCESS } = bootstrapActionTypes;

export const ASYNC_SCENARIO_LOAD_WINDOW_MS = 15_000;

// Module-level ephemeral state for async scenario load lifecycle —
// intentionally not in Redux since nothing else subscribes to it.
let asyncScenarioLoadTimer: ReturnType<typeof setTimeout> | null = null;
let asyncScenarioLoadStartedAt: number | null = null;
let asyncScenarioLoadCompletionFired: boolean = false;

/**
 * Test helper — resets module-level async-scenario-load state.
 * Tests must call this in beforeEach or state leaks across tests.
 */
export function __resetAsyncScenarioLoadState(): void {
    if (asyncScenarioLoadTimer) {
        clearTimeout(asyncScenarioLoadTimer);
    }
    asyncScenarioLoadTimer = null;
    asyncScenarioLoadStartedAt = null;
    asyncScenarioLoadCompletionFired = false;
}

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    // For SCENARIO_HYDRATE_RECEIVED, next() must be called AFTER the switch entry
    // so offerMiddleware updates initializedScopesOrder before we check completion.
    if (action.type !== SCENARIO_HYDRATE_RECEIVED) {
        next(action);
    }

    switch (action.type) {
        case BOOTSTRAP_INITIAL_LOAD: {
            const enableMVMSLoadAsync = getGlobalFeatureToggle('enableMVMSLoadAsync');
            if (!enableMVMSLoadAsync) break;

            const manager = new SocketManagerV2({
                dealExchangeId: getDealXgId(),
                messageCallback: /* istanbul ignore next */ (payload: any = {}) => {
                    store.dispatch(socketActionCreators.dispatchEvent(store.getState(), payload));
                },
                selectors: {
                    getDealRefId: /* istanbul ignore next */ () => {
                        const state = store.getState();
                        return offerSelectors.getDealRefId(state);
                    }
                },
                onConnectionFailure: /* istanbul ignore next */ (failed: boolean) => {
                    store.dispatch(networkActions.wsConnectionFailed(failed));
                }
            });

            (window as any).drSocketManager = manager;
            manager.start(true);

            break;
        }
        case BOOTSTRAP_SUCCESS: {
            const enableMVMSLoadAsync = getGlobalFeatureToggle('enableMVMSLoadAsync');
            if (enableMVMSLoadAsync) {
                const state = store.getState();
                const manager = (window as any).drSocketManager as SocketManagerV2;
                manager.reinitialize(
                    {
                        enableOptionalPbcUi: featureToggleSelectors.enableOptionalPbcUi(state),
                        enableGenerateContracts: dealerSelectors.getEnableGenerateContracts(state),
                        isDisplayContractStatusEnabled: featureToggleSelectors.isDisplayContractStatusEnabled(state)
                    },
                    {
                        connectionFailureLogThreshold: configSelectors.getConnectionFailureLogThreshold(action.payload),
                        healthCheckInterval: configSelectors.getHealthCheckInterval(action.payload)
                    }
                );

                // Start 15s timer to detect if not all expected scenarios arrive via WebSocket.
                // Fires MV:AsyncScenarioLoadTimeout if not all received before the window closes.
                const expectedIds = getAsyncScenarioExpectedIds(store.getState());
                if (expectedIds.length > 0) {
                    asyncScenarioLoadStartedAt = performance.now();
                    asyncScenarioLoadCompletionFired = false;
                    asyncScenarioLoadTimer = setTimeout(() => {
                        /* istanbul ignore next -- defensive guard: completion fires clearTimeout synchronously, so this branch is unreachable under fake timers */
                        if (asyncScenarioLoadCompletionFired) return;
                        const timeoutState = store.getState();
                        const expected = getAsyncScenarioExpectedIds(timeoutState);
                        const received = getAsyncScenarioReceivedIds(timeoutState);
                        const missing = getAsyncScenarioMissingIds(timeoutState);
                        const dealId = offerReduxSelectors.getDealXgId(timeoutState);
                        /* istanbul ignore next -- defensive optional chain: manager is always non-null here */
                        const sessionId = manager?.sessionId;
                        logNewRelicPageAction('MV:AsyncScenarioLoadTimeout', {
                            sessionId,
                            dealId,
                            expectedCount: expected.length,
                            receivedCount: received.length,
                            missingScenarioIds: missing
                        });
                        asyncScenarioLoadCompletionFired = true;
                        asyncScenarioLoadTimer = null;
                        store.dispatch({ type: SCENARIOS_INGEST_COMPLETE });
                    }, ASYNC_SCENARIO_LOAD_WINDOW_MS);
                }
            } else {
                const wsUrl = configSelectors.getWebsocketUrl(action.payload);
                const connectionFailureLogThreshold = configSelectors.getConnectionFailureLogThreshold(action.payload);
                const healthCheckInterval = configSelectors.getHealthCheckInterval(action.payload);
                const state = store.getState();
                const enableOptionalPbcUi = featureToggleSelectors.enableOptionalPbcUi(state);
                const enableGenerateContracts = dealerSelectors.getEnableGenerateContracts(state);
                const isDisplayContractStatusEnabled = featureToggleSelectors.isDisplayContractStatusEnabled(state);

                const manager = new SocketManager({
                    wsUrl,
                    messageCallback: /* istanbul ignore next */ (payload: any = {}) => {
                        const state = store.getState();
                        store.dispatch(socketActionCreators.dispatchEvent(state, payload));
                    },
                    dealExchangeId: offerReduxSelectors.getDealXgId(state),
                    selectors: {
                        getDealRefId: /* istanbul ignore next */ () => {
                            const state = store.getState();
                            return offerSelectors.getDealRefId(state);
                        }
                    },
                    toggles: {
                        enableOptionalPbcUi,
                        enableGenerateContracts,
                        isDisplayContractStatusEnabled
                    },
                    onConnectionFailure: /* istanbul ignore next */ (failed: boolean) => {
                        store.dispatch(networkActions.wsConnectionFailed(failed));
                    },
                    connectionFailureLogThreshold,
                    healthCheckInterval
                });

                // use this instead of creating IWindow interface
                (window as any).drSocketManager = manager;
                manager.start(true);
            }

            break;
        }
        case SCENARIO_HYDRATE_RECEIVED: {
            // Call next FIRST so offerMiddleware processes the action and updates
            // initializedScopesOrder before we check hasAsyncScenarioLoadCompleted.
            next(action);

            if (!asyncScenarioLoadCompletionFired && hasAsyncScenarioLoadCompleted(store.getState())) {
                asyncScenarioLoadCompletionFired = true;
                if (asyncScenarioLoadTimer) {
                    clearTimeout(asyncScenarioLoadTimer);
                    asyncScenarioLoadTimer = null;
                }
                const completionState = store.getState();
                const expected = getAsyncScenarioExpectedIds(completionState);
                const received = getAsyncScenarioReceivedIds(completionState);
                const dealId = offerReduxSelectors.getDealXgId(completionState);
                /* istanbul ignore next -- defensive optional chain: drSocketManager is always set by the time completion fires */
                const sessionId = (window as any).drSocketManager?.sessionId;
                const durationToCompletionMs =
                    asyncScenarioLoadStartedAt !== null ? performance.now() - asyncScenarioLoadStartedAt : null;
                logNewRelicPageAction('MV:AsyncScenarioLoadComplete', {
                    sessionId,
                    dealId,
                    expectedCount: expected.length,
                    receivedCount: received.length,
                    durationToCompletionMs
                });
                store.dispatch({ type: SCENARIOS_INGEST_COMPLETE });
            }

            break;
        }
        case OFFER_SAVE_SUCCESS: {
            // Quote-mode: the deal has no dxg id at app load, so the socket starts
            // subscribed to an empty id. saveOffer returns the real dxg id here — re-point
            // the running socket and re-subscribe so we receive events for the new deal.
            if (!featureToggleSelectors.isManagerViewQuoteModeEnabled(store.getState())) {
                break;
            }
            const dealXgId = action.payload.dealXgId;
            const manager = (window as any).drSocketManager as SocketManager | SocketManagerV2 | undefined;
            if (dealXgId && manager) {
                manager.updateChangedDealExchangeId(dealXgId);
            }
            break;
        }
        default:
            break;
    }
};
